import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';

const isImage = (filename) => /\.(jpg|jpeg|png)$/i.test(filename || '');

const DocViewer = ({ url, filename, urls, filenames, initialIndex = 0, onClose }) => {
  // Backward compatible: callers can pass a single url/filename, or a list for a gallery.
  const items = Array.isArray(urls) && urls.length ? urls : (url ? [url] : []);
  const names = Array.isArray(filenames) && filenames.length ? filenames : (filename ? [filename] : []);

  const [index, setIndex] = useState(Math.min(initialIndex, Math.max(items.length - 1, 0)));
  const [blobUrl, setBlobUrl] = useState(null);
  const [imgError, setImgError] = useState(false);

  const currentUrl = items[index] || null;
  const currentFilename = names[index] || names[0] || 'Document';
  const hasMultiple = items.length > 1;

  useEffect(() => {
    setIndex(Math.min(initialIndex, Math.max(items.length - 1, 0)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, JSON.stringify(urls)]);

  useEffect(() => {
    setBlobUrl(null);
    setImgError(false);
    if (!currentUrl || !isImage(currentFilename) || currentUrl.startsWith('blob:')) return;

    // Fetch the image via JS so Android WebView doesn't block it as mixed content
    // (the app is served from https://localhost but images come from http://).
    let revoked = false;
    let objUrl = null;
    fetch(currentUrl)
      .then((r) => r.blob())
      .then((blob) => {
        if (revoked) return;
        objUrl = URL.createObjectURL(blob);
        setBlobUrl(objUrl);
      })
      .catch(() => setImgError(true));

    return () => {
      revoked = true;
      if (objUrl) URL.revokeObjectURL(objUrl);
    };
  }, [currentUrl, currentFilename]);

  if (!currentUrl) return null;

  const imgSrc = currentUrl.startsWith('blob:') ? currentUrl : blobUrl;

  const goPrev = (e) => { e.stopPropagation(); setIndex((i) => (i - 1 + items.length) % items.length); };
  const goNext = (e) => { e.stopPropagation(); setIndex((i) => (i + 1) % items.length); };

  return ReactDOM.createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 3000,
        background: 'rgba(0,0,0,0.92)',
        display: 'flex', flexDirection: 'column',
      }}
    >
      {/* Header bar */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px', flexShrink: 0,
          background: 'rgba(0,0,0,0.5)',
        }}
      >
        <span style={{ color: '#fff', fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 'calc(100% - 48px)' }}>
          {currentFilename}{hasMultiple ? ` (${index + 1}/${items.length})` : ''}
        </span>
        <button
          onClick={onClose}
          style={{
            background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: 8,
            color: '#fff', width: 36, height: 36, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}
        >
          <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      </div>

      {/* Document area */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', position: 'relative' }}
      >
        {hasMultiple && (
          <button
            onClick={goPrev}
            aria-label="Previous"
            style={{
              position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 1,
              background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: '50%',
              color: '#fff', width: 40, height: 40, cursor: 'pointer', fontSize: 20,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            ‹
          </button>
        )}

        {isImage(currentFilename) ? (
          imgError ? (
            <span style={{ color: '#aaa', fontSize: 14 }}>Failed to load image.</span>
          ) : imgSrc ? (
            <img
              src={imgSrc}
              alt={currentFilename}
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
            />
          ) : (
            <span style={{ color: '#aaa', fontSize: 14 }}>Loading…</span>
          )
        ) : currentUrl.startsWith('blob:') ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: 32 }}>
            <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
            <span style={{ color: '#aaa', fontSize: 14, textAlign: 'center', lineHeight: 1.5 }}>
              PDF preview is not available before uploading.{'\n'}Save the document first, then preview it.
            </span>
          </div>
        ) : (
          <iframe
            src={`https://docs.google.com/viewer?embedded=true&url=${encodeURIComponent(currentUrl)}`}
            title={currentFilename}
            style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }}
          />
        )}

        {hasMultiple && (
          <button
            onClick={goNext}
            aria-label="Next"
            style={{
              position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 1,
              background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: '50%',
              color: '#fff', width: 40, height: 40, cursor: 'pointer', fontSize: 20,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            ›
          </button>
        )}
      </div>
    </div>,
    document.body
  );
};

export default DocViewer;
